import React, { Fragment, useMemo, useRef } from "react";
import { MANIFESTO, PRINCIPLES, TICKER } from "../content";
import Ticker from "./Ticker";
import useRafScroll from "../hooks/useRafScroll";
import useReducedMotion from "../hooks/useReducedMotion";

/**
 * The manifesto is not revealed, it is *read to you*: the statement pins to the
 * viewport and each word lights as your scroll position passes over it. The
 * scrub writes opacity straight onto the DOM nodes — putting 60-odd words
 * through React state every frame would be a reconciliation disaster.
 */
export default function Method() {
  const sectionRef = useRef(null);
  const wordsRef = useRef([]);
  const reduced = useReducedMotion();

  const words = useMemo(() => MANIFESTO.split(" "), []);

  useRafScroll(() => {
    const section = sectionRef.current;
    const nodes = wordsRef.current;
    if (!section || !nodes.length || reduced) return;

    const runway = section.querySelector(".method__runway");
    if (!runway) return;

    const rect = runway.getBoundingClientRect();
    const travel = runway.offsetHeight - window.innerHeight;
    const p = Math.max(0, Math.min(1, -rect.top / travel));

    // Spread the read across the first 88% of the pin, then hold.
    const head = p * 1.14 * nodes.length;

    for (let i = 0; i < nodes.length; i += 1) {
      const node = nodes[i];
      if (!node) continue;
      const local = Math.max(0, Math.min(1, head - i));
      node.style.opacity = String(0.14 + local * 0.86);
      node.style.filter = `blur(${(1 - local) * 3}px)`;
    }
  });

  return (
    <section className="method" id="method" data-act="method" ref={sectionRef}>
      <div className="method__runway">
        <div className="method__pin">
          <div className="method__head">
            <h2 className="mono">02 — Method</h2>
            <span className="mono">A studio position</span>
          </div>

          <p className="method__statement">
            {words.map((w, i) => (
              <Fragment key={`${w}-${i}`}>
                <span
                  className="method__word"
                  ref={(el) => {
                    wordsRef.current[i] = el;
                  }}
                >
                  {w}
                </span>{" "}
              </Fragment>
            ))}
          </p>
        </div>
      </div>

      <div className="tickers" aria-hidden="true">
        <Ticker items={TICKER} dir={1} tilt={-2.6} />
        <Ticker items={["Break the loop", "Ship the thing", "Count the doing"]} dir={-1} tilt={2.6} />
      </div>

      <div className="principles">
        {PRINCIPLES.map((p, i) => (
          <article className="principle" key={p.n} data-reveal="up" style={{ "--d": `${i * 90}ms` }}>
            <span className="principle__n mono">{p.n}</span>
            <h3 className="principle__title">{p.title}</h3>
            <p className="principle__body">{p.body}</p>
            <span className="principle__rule" />
          </article>
        ))}
      </div>
    </section>
  );
}
